import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Input,
  Form,
  Spin,
  Tooltip,
  Badge,
  message,
} from "antd";
import {
  EyeOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  deleteHtmlTemplate,
  createHtmlTemplate,
  getHtmlTemplatesPaginated,
  getHtmlTemplateById,
  duplicateHtmlTemplate,
  seedDefaultTemplates,
  checkDefaultTemplatesSeeded,
} from "@api/email/htmlTemplate";

const { Search } = Input;

const PulseHtmlTemplatesList = ({ history }) => {
  // State variables
  const [htmlTemplates, setHtmlTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalHtmlTemplates: 0,
  });

  // Create modal state
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  // State for seeding default templates
  const [seedStatus, setSeedStatus] = useState({
    seeded: false,
    count: 0,
    total: 0,
  });
  const [seedLoading, setSeedLoading] = useState(false);

  // State for preview modal
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Check if default templates are seeded on mount
  useEffect(() => {
    const checkSeedStatus = async () => {
      try {
        const res = await checkDefaultTemplatesSeeded();
        setSeedStatus(res.data);
      } catch (err) {
        console.error("Error checking seed status", err);
      }
    };
    checkSeedStatus();
  }, []);

  // Fetch HTML templates on component mount and when pagination/search changes
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({
      current: page,
      pageSize: pageSize,
      totalHtmlTemplates: 0,
    });

    fetchHtmlTemplates(page, pageSize, search);
  }, []);

  // Function to fetch HTML templates
  const fetchHtmlTemplates = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getHtmlTemplatesPaginated(page, pageSize, searchText);

      setHtmlTemplates(res.data.htmlTemplates || []);
      setPagination((p) => ({
        ...p,
        totalHtmlTemplates: res.data.paginationData?.totalHtmlTemplates,
        current: res.data.paginationData?.page,
        pageSize: res.data.paginationData?.perPage,
      }));
    } catch (err) {
      console.error("Fetch HTML templates error", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchHtmlTemplates(page, pageSize, searchText);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 });
    fetchHtmlTemplates(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to show create modal
  const showCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  // Function to handle create modal ok
  const handleCreateModalOk = () => {
    createForm.submit();
  };

  // Function to handle create modal cancel
  const handleCreateModalCancel = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
  };

  // Function to handle form submission
  const onCreateFormFinish = async (values) => {
    setCreateLoading(true);
    try {
      const response = await createHtmlTemplate(values);
      const newTemplateId = response.data._id;
      handleCreateModalCancel();
      history.push(`/super-admin/pulse-reports/html-template/${newTemplateId}/create`);
    } catch (error) {
      console.error("Error creating HTML template", error);
      Modal.error({
        title: "Creation Failed",
        content: "Failed to create HTML template. Please try again.",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // Function to handle HTML template update
  const handleHtmlTemplateUpdate = (id) => {
    history.push(`/super-admin/pulse-reports/html-template/${id}/update`);
  };

  // Function to handle HTML template delete
  const handleHtmlTemplateDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this HTML template?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteHtmlTemplate(id);
          fetchHtmlTemplates(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error deleting HTML template", error);
        }
      },
    });
  };

  // Function to handle HTML template duplicate
  const handleHtmlTemplateDuplicate = (id) => {
    Modal.confirm({
      title: "Are you sure you want to duplicate this HTML template?",
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: async () => {
        try {
          await duplicateHtmlTemplate(id);
          fetchHtmlTemplates(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error duplicating HTML template", error);
        }
      },
    });
  };

  // Function to handle HTML template preview
  const handleHtmlTemplatePreview = async (id) => {
    setPreviewLoading(true);
    setPreviewVisible(true);
    try {
      const res = await getHtmlTemplateById(id);
      setPreviewTemplate(res.data);
    } catch (error) {
      console.error("Error fetching template for preview", error);
      message.error("Failed to load template preview");
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Function to handle seeding default templates
  const handleSeedTemplates = async (force = false) => {
    setSeedLoading(true);
    try {
      const res = await seedDefaultTemplates(force);
      const { created, updated, skipped } = res.data;

      message.success(
        `Templates seeded: ${created} created, ${updated} updated, ${skipped} skipped`
      );

      // Refresh the seed status and templates list
      const statusRes = await checkDefaultTemplatesSeeded();
      setSeedStatus(statusRes.data);
      fetchHtmlTemplates(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      console.error("Error seeding templates", error);
      message.error("Failed to seed default templates");
    } finally {
      setSeedLoading(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) =>
        name.length > 25 ? `${name.substring(0, 25)}...` : name,
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (user) => user?.name || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Tooltip title="Preview template">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              style={{ marginRight: "10px" }}
              onClick={() => handleHtmlTemplatePreview(record._id)}
            >
              Preview
            </Button>
          </Tooltip>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() => handleHtmlTemplateUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            style={{ marginRight: "10px" }}
            onClick={() => handleHtmlTemplateDelete(record._id)}
          >
            Delete
          </Button>
          <Button
            type="ghost"
            size="small"
            onClick={() => handleHtmlTemplateDuplicate(record._id)}
          >
            Duplicate
          </Button>
        </>
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", height: "100vh", overflowY: "hidden" }}>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <h3>HTML Templates List</h3>
        </Col>
        <Col style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Seed Default Templates Button */}
          <Tooltip
            title={
              seedStatus.seeded
                ? `${seedStatus.count}/${seedStatus.total} default templates seeded. Click to update.`
                : `Seed ${seedStatus.total} default templates`
            }
          >
            <Badge
              count={
                seedStatus.seeded ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : (
                  seedStatus.total
                )
              }
              offset={[-5, 5]}
            >
              <Button
                icon={
                  seedLoading ? <SyncOutlined spin /> : <DatabaseOutlined />
                }
                onClick={() => handleSeedTemplates(seedStatus.seeded)}
                loading={seedLoading}
              >
                {seedStatus.seeded ? "Update Templates" : "Seed Templates"}
              </Button>
            </Badge>
          </Tooltip>

          {/* View Pulse Reports Button */}
          <Button
            type="ghost"
            onClick={() => history.push("/super-admin/pulse-reports")}
          >
            View Pulse Reports
          </Button>

          {/* View Pulse Report Campaigns Button */}
          <Button
            type="ghost"
            onClick={() => history.push("/super-admin/pulse-reports/list")}
          >
            View Campaigns
          </Button>

          {/* Create HTML Template Button */}
          <Button type="primary" onClick={showCreateModal}>
            Create HTML Template
          </Button>

          {/* Search */}
          <Search
            placeholder="Search by name"
            style={{ width: "300px" }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>
      </Row>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={htmlTemplates}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.totalHtmlTemplates,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} templates`,
          onChange: handlePaginationChange,
        }}
      />

      {/* Create HTML Template Modal */}
      <Modal
        title="Create New HTML Template"
        open={isCreateModalVisible}
        onOk={handleCreateModalOk}
        onCancel={handleCreateModalCancel}
        confirmLoading={createLoading}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={onCreateFormFinish}
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: "Please enter a template name" },
            ]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview HTML Template Modal */}
      <Modal
        title={previewTemplate?.name || "Template Preview"}
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewTemplate(null);
        }}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setPreviewVisible(false);
              handleHtmlTemplateUpdate(previewTemplate?._id);
            }}
          >
            Edit Template
          </Button>,
        ]}
        width={800}
        bodyStyle={{ padding: 0, height: "70vh" }}
        centered
      >
        {previewLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Spin size="large" tip="Loading preview..." />
          </div>
        ) : previewTemplate?.compiledHtml ? (
          <iframe
            srcDoc={previewTemplate.compiledHtml}
            title="Email Preview"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "#999",
            }}
          >
            No preview available. Template may not have compiled HTML.
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default PulseHtmlTemplatesList;
